// @flow
import React from 'react';
import {connect} from 'react-redux';

import Filters from '@homeaway/react-search-filters';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import FormModal from '../FormModal/FormModal';

import {media} from '../../utils/breakpoints';

import routerActions from '../../actions/router';
import overlayActions from '../../actions/overlay';
import searchParamsActions from '../../actions/searchParams';
import {FILTERS_MODAL} from '../../actions/modals';

import type {State as ReduxState} from '../../reducers/index';
import type {Action as OverlayAction} from '../../actions/overlay';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {FilterGroupType} from '@homeaway/react-search-filters';
import type {LinguaProps} from '../withLingua/withLingua';

import {instantBookFilter, getCancellationFilterId} from '../../utils/filters';

export type FilterDispatchProps = {
    filter: typeof routerActions.filter,
    onDone: () => void,
    resetInputParams: typeof searchParamsActions.resetInputParams,
    syncInputParams: typeof searchParamsActions.syncInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    updateMultipleFilterCheck?: typeof searchParamsActions.updateMultipleFilterCheck,
    updateInputParams: typeof searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity: typeof searchParamsActions.updatePriceInputValuesValidity
};

export type FiltersProps = {
    currency: string,
    hasDates: boolean,
    inputParamsAreChanged: boolean,
    inputParamsAreDefault: boolean,
    isMobile: boolean,
    locale: string,
    numFiltersPanel?: ?number,
    priceInputValuesAreValid: boolean,
    viewportHeight: number
};

export type FilterFormProps = {
    filterGroups: FilterGroupType[],
    inputParams: SearchParamsType,
    modalIsOpen: boolean,
    resultsCount?: ?number
} & FiltersProps &
    FilterDispatchProps;

export type FilterBarProps = {
    allFiltersButtonOnly?: Boolean,
    allFiltersButtonCustomTrigger?: Element,
    currentBedrooms: number,
    exactBedroomsMatch: boolean,
    hideOverlay: () => OverlayAction,
    inputMaxBedrooms: number,
    inputMaxPrice: number,
    inputMinBedrooms: number,
    inputMinPrice: number,
    isCancellationPoliciesFilterSelected: boolean,
    isMoreFiltersSelected: boolean,
    isOverlayVisible: boolean,
    maxBedrooms: number,
    maxPrice: number,
    minBedrooms: number,
    minPrice: number,
    searchResultsLoading: boolean,
    showOverlay: () => OverlayAction,
    siteName: string,
    topBannersHeight: number
} & FiltersProps &
    FilterDispatchProps;

export type FilterContainerProps = FilterBarProps &
    FilterFormProps &
    FilterDispatchProps &
    LinguaProps;

export function FiltersContainer(props: FilterContainerProps) {
    return (
        <Filters
            {...props}
            filterCountDataProvider={FilteredCountDataProvider}
            modal={FormModal}
        />
    );
}

export const mapStateToProps = (state: ReduxState) => {
    // select params that only apply to this component
    const {
        modals,
        overlay: {showOverlay: isOverlayVisible},
        searchParams: {
            inputParams,
            inputParamsAreChanged,
            inputParamsAreDefault,
            numFiltersPanel,
            params,
            params: {
                filter,
                maxBathrooms,
                maxBedrooms,
                maxPrice,
                minBathrooms,
                minBedrooms,
                minPrice
            },
            priceInputValuesAreValid,
            selectedFilters
        },
        system: {currency, siteName, fullLocale},
        topBanners: {height: topBannersHeight},
        viewport: {height, breakpoint}
    } = state;
    return {
        bedroomsExactMatch: inputParams.minBedrooms === inputParams.maxBedrooms,
        canFit3Columns: media(breakpoint, {minWidth: 'MD_MIN'}),
        canFit4Columns: media(breakpoint, {minWidth: 'LG_MIN'}),
        currency,
        hasDates: !!(inputParams.arrival && inputParams.departure),
        inputMaxBathrooms: inputParams.maxBathrooms,
        inputMaxBedrooms: inputParams.maxBedrooms,
        inputMaxPrice: inputParams.maxPrice,
        inputMinBathrooms: inputParams.minBathrooms,
        inputMinBedrooms: inputParams.minBedrooms,
        inputMinPrice: inputParams.minPrice,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreDefault,
        isMobile: !media(breakpoint, {minWidth: 'SM_MIN'}),
        isCancellationPoliciesFilterSelected: !!getCancellationFilterId(filter),
        isMoreFiltersSelected: !!(
            (minBathrooms && minBathrooms > 0) ||
            (maxBathrooms && maxBathrooms > 0) ||
            (filter && filter.length > 0 && !instantBookFilter(filter))
        ),
        isOverlayVisible,
        locale: fullLocale,
        maxBathrooms,
        maxBedrooms,
        maxPrice,
        minBathrooms,
        minBedrooms,
        minPrice,
        modalIsOpen: modals.FILTERS.isOpen,
        numFiltersPanel,
        params,
        priceInputValuesAreValid,
        selectedFilters,
        siteName,
        topBannersHeight,
        viewportHeight: height
    };
};

export const mapDispatchToProps = {
    filter: routerActions.filter,
    hideOverlay: () => overlayActions.toggleOverlay(false),
    onDone: () => routerActions.popModal(FILTERS_MODAL),
    resetInputParams: searchParamsActions.resetInputParams,
    showOverlay: () => overlayActions.toggleOverlay(true),
    syncInputParams: searchParamsActions.syncInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    updateMultipleFilterCheck: searchParamsActions.updateMultipleFilterCheck,
    updateInputParams: searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity:
        searchParamsActions.updatePriceInputValuesValidity,
    clearFilters: searchParamsActions.clearFilters
};

const ConnectedFiltersContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(FiltersContainer);

export default ConnectedFiltersContainer;
